import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  InputAdornment,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Grid,
  Button,
  Divider,
  Chip,
  Collapse,
  useTheme,
  IconButton,
  alpha,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { 
  CandidateFilters as CandidateFiltersType, 
  initialFilters, 
  CandidateStatus, 
  CandidateExperience,
  statusColors,
  experienceLabels
} from '../../types/Candidate';
import { getAvailableDepartments } from '../../services/candidateService';
import { useThemeContext } from '../../context/ThemeContext';

interface CandidateFiltersProps {
  filters: CandidateFiltersType;
  onFilterChange: (filters: CandidateFiltersType) => void;
  activeFilterCount: number;
}

const CandidateFilters: React.FC<CandidateFiltersProps> = ({
  filters,
  onFilterChange,
  activeFilterCount,
}) => {
  const theme = useTheme();
  const { mode } = useThemeContext();
  const isDark = mode === 'dark';
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const departments = getAvailableDepartments();
  
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({
      ...filters,
      search: e.target.value,
    });
  };
  
  const handleStatusChange = (status: CandidateStatus) => {
    const newStatuses = filters.status.includes(status)
      ? filters.status.filter(s => s !== status)
      : [...filters.status, status];
      
    onFilterChange({
      ...filters,
      status: newStatuses,
    });
  };
  
  const handleExperienceChange = (experience: CandidateExperience) => {
    const newExperience = filters.experience.includes(experience)
      ? filters.experience.filter(e => e !== experience)
      : [...filters.experience, experience];
      
    onFilterChange({
      ...filters,
      experience: newExperience,
    });
  };
  
  const handleDepartmentChange = (department: string) => {
    const newDepartments = filters.department.includes(department)
      ? filters.department.filter(d => d !== department)
      : [...filters.department, department];
      
    onFilterChange({
      ...filters,
      department: newDepartments,
    });
  };
  
  const handleStartDateChange = (date: Date | null) => {
    onFilterChange({
      ...filters,
      dateRange: {
        ...filters.dateRange,
        start: date ? date.toISOString().split('T')[0] : null,
      },
    });
  };
  
  const handleEndDateChange = (date: Date | null) => {
    onFilterChange({
      ...filters,
      dateRange: {
        ...filters.dateRange,
        end: date ? date.toISOString().split('T')[0] : null,
      },
    });
  };
  
  const handleResetFilters = () => {
    onFilterChange(initialFilters);
  };
  
  const toggleFiltersExpanded = () => {
    setFiltersExpanded(!filtersExpanded);
  };

  const statuses: CandidateStatus[] = ['applied', 'screening', 'interview', 'technical', 'offer', 'hired', 'rejected'];
  const experiences: CandidateExperience[] = ['entry', 'junior', 'mid', 'senior', 'lead', 'executive'];

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 3,
        border: `1px solid ${isDark ? alpha('#fff', 0.1) : alpha('#000', 0.1)}`,
        borderRadius: 2,
      }}
    >
      {/* Search bar and filter toggle */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Search candidates by name, email, position or department"
          variant="outlined"
          size="small"
          value={filters.search}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="primary" />
              </InputAdornment>
            ),
            sx: {
              borderRadius: 2,
              backgroundColor: isDark 
                ? alpha(theme.palette.primary.main, 0.05) 
                : alpha(theme.palette.primary.main, 0.03),
            }
          }}
        />
        
        <Button
          variant={filtersExpanded ? "contained" : "outlined"}
          color="primary"
          startIcon={<FilterListIcon />}
          endIcon={filtersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          onClick={toggleFiltersExpanded}
          sx={{ 
            ml: 2, 
            borderRadius: 2,
            minWidth: '130px',
            textTransform: 'none',
          }}
        >
          {filtersExpanded ? 'Hide Filters' : 'Show Filters'}
          {activeFilterCount > 0 && !filtersExpanded && (
            <Chip
              label={activeFilterCount}
              color="primary"
              size="small"
              sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
            />
          )}
        </Button>
        
        {activeFilterCount > 0 && (
          <Button
            variant="text"
            color="primary"
            startIcon={<ClearIcon />}
            onClick={handleResetFilters}
            sx={{ ml: 2, borderRadius: 2, textTransform: 'none' }}
          >
            Clear All
          </Button>
        )}
      </Box>
      
      {/* Advanced filters */}
      <Collapse in={filtersExpanded}>
        <Divider sx={{ my: 2 }} />
        
        <Grid container spacing={3}>
          {/* Status filter */}
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Status
            </Typography>
            <FormGroup>
              {statuses.map((status) => (
                <FormControlLabel
                  key={status}
                  control={
                    <Checkbox
                      checked={filters.status.includes(status)}
                      onChange={() => handleStatusChange(status)}
                      size="small"
                      sx={{
                        color: alpha(statusColors[status], 0.7),
                        '&.Mui-checked': {
                          color: statusColors[status],
                        },
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                        {status}
                      </Typography>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: statusColors[status],
                          ml: 1,
                        }}
                      />
                    </Box>
                  }
                />
              ))}
            </FormGroup>
          </Grid>
          
          {/* Experience filter */}
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Experience Level
            </Typography>
            <FormGroup>
              {experiences.map((exp) => (
                <FormControlLabel
                  key={exp}
                  control={
                    <Checkbox
                      checked={filters.experience.includes(exp)}
                      onChange={() => handleExperienceChange(exp)}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {experienceLabels[exp]}
                    </Typography>
                  }
                />
              ))}
            </FormGroup>
          </Grid>
          
          {/* Department filter */}
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Department
            </Typography>
            <FormGroup>
              {departments.map((department) => (
                <FormControlLabel
                  key={department}
                  control={
                    <Checkbox
                      checked={filters.department.includes(department)}
                      onChange={() => handleDepartmentChange(department)}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {department}
                    </Typography>
                  }
                />
              ))}
            </FormGroup>
          </Grid>
          
          {/* Date range filter */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Application Date Range
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <DatePicker
                  label="From"
                  value={filters.dateRange.start ? new Date(filters.dateRange.start) : null}
                  onChange={handleStartDateChange}
                  slotProps={{ 
                    textField: { 
                      size: 'small',
                      fullWidth: true,
                      sx: { borderRadius: 2 }
                    } 
                  }}
                />
                <DatePicker
                  label="To"
                  value={filters.dateRange.end ? new Date(filters.dateRange.end) : null}
                  onChange={handleEndDateChange}
                  slotProps={{ 
                    textField: { 
                      size: 'small',
                      fullWidth: true,
                      sx: { borderRadius: 2 }
                    } 
                  }}
                />
              </Box>
            </LocalizationProvider>
          </Grid>
        </Grid>
      </Collapse>
      
      {/* Active filters display */}
      {activeFilterCount > 0 && !filtersExpanded && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
          {filters.status.map((status) => (
            <Chip
              key={`status-${status}`}
              label={status}
              onDelete={() => handleStatusChange(status)}
              size="small"
              sx={{
                bgcolor: alpha(statusColors[status], 0.1),
                color: statusColors[status],
                borderRadius: 1.5,
              }}
            />
          ))}
          
          {filters.experience.map((exp) => (
            <Chip
              key={`exp-${exp}`}
              label={experienceLabels[exp]}
              onDelete={() => handleExperienceChange(exp)}
              size="small"
              sx={{ borderRadius: 1.5 }}
            />
          ))}
          
          {filters.department.map((dept) => (
            <Chip
              key={`dept-${dept}`}
              label={dept}
              onDelete={() => handleDepartmentChange(dept)}
              size="small"
              sx={{ borderRadius: 1.5 }}
            />
          ))}
          
          {filters.dateRange.start && (
            <Chip
              label={`From: ${filters.dateRange.start}`}
              onDelete={() => handleStartDateChange(null)}
              size="small"
              sx={{ borderRadius: 1.5 }}
            />
          )}
          
          {filters.dateRange.end && (
            <Chip
              label={`To: ${filters.dateRange.end}`}
              onDelete={() => handleEndDateChange(null)}
              size="small"
              sx={{ borderRadius: 1.5 }}
            />
          )}
        </Box>
      )}
    </Paper>
  );
};

export default CandidateFilters;